import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listOrgUnits,
  getOrgUnit,
  createOrgUnit,
  updateOrgUnit,
  deleteOrgUnit,
  setSiteOrgUnit,
  setUserGroups,
  ListOrgUnitsParams,
} from '../services/adminApi';
import type {
  CreateOrgUnitInput,
  UpdateOrgUnitInput,
} from '../types/orgUnit';

export function useOrgUnits(params: ListOrgUnitsParams = {}) {
  return useQuery({
    queryKey: ['org-units', params],
    queryFn: () => listOrgUnits(params),
    staleTime: 10_000,
  });
}

export function useOrgUnit(orgUnitId: string | null) {
  return useQuery({
    queryKey: ['org-units', orgUnitId],
    queryFn: () => getOrgUnit(orgUnitId!),
    enabled: !!orgUnitId,
    staleTime: 5_000,
  });
}

export function useCreateOrgUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrgUnitInput) => createOrgUnit(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-units'] });
    },
  });
}

export function useUpdateOrgUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: UpdateOrgUnitInput }) =>
      updateOrgUnit(id, patch),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['org-units'] });
      qc.invalidateQueries({ queryKey: ['org-units', vars.id] });
    },
  });
}

export function useDeleteOrgUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, cascade }: { id: string; cascade: boolean }) =>
      deleteOrgUnit(id, cascade),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['org-units'] });
      qc.invalidateQueries({ queryKey: ['sites'] });
    },
  });
}

export function useSetSiteOrgUnit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ siteId, orgUnitId }: { siteId: string; orgUnitId: string | null }) =>
      setSiteOrgUnit(siteId, orgUnitId),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['sites'] });
      if (vars.orgUnitId) {
        qc.invalidateQueries({ queryKey: ['org-units', vars.orgUnitId] });
      }
    },
  });
}

export function useSetUserGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, orgUnitIds }: { userId: string; orgUnitIds: string[] }) =>
      setUserGroups(userId, orgUnitIds),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['org-units'] });
    },
  });
}
